import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DayPilot } from '@daypilot/daypilot-lite-react';
import { initials } from '../shared/ui/InitialsAvatar';

function toLocalDate(val) {
  if (!val) return '';
  try {
    const d = new DayPilot.Date(val);
    return d.toString('yyyy-MM-dd');
  } catch { return ''; }
}

function fromLocalDate(val) {
  if (!val) return '';
  if (val.includes('T')) return val;
  return val + 'T00:00:00';
}

/* ── Resource selector with avatar + search ── */
function ResourceField({ value, onChange, options, inputRef }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const selected = options.find((o) => o.id === value);
  const filtered = options.filter((o) =>
    o.name.toLowerCase().includes(search.toLowerCase()),
  );

  const renderAvatar = (opt, size = 24) => (
    <div
      style={{
        width: size, height: size, borderRadius: '50%',
        background: 'var(--color-iris-100, #e3e3fd)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: size * 0.42, fontWeight: 700,
        color: 'var(--color-iris-700, #4a3fc7)', flexShrink: 0,
      }}
    >
      {initials(opt.name || '?')}
    </div>
  );

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        ref={inputRef}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2.5 w-full text-sm border-2 border-gray-300 rounded-lg py-2.5 px-3 bg-white cursor-pointer shadow-sm transition-all hover:border-gray-400 focus:border-iris-500 focus:ring-2 focus:ring-iris-500/20 focus:outline-none text-left"
      >
        {selected ? (
          <>
            {renderAvatar(selected, 28)}
            <span className="truncate font-medium">{selected.name}</span>
          </>
        ) : (
          <span className="text-gray-400">Sélectionner un collaborateur…</span>
        )}
        <svg className="ml-auto h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher…"
              className="w-full text-sm border border-gray-300 rounded-md py-1.5 px-2.5 focus:border-iris-500 focus:ring-1 focus:ring-iris-500/20 focus:outline-none"
            />
          </div>
          <div className="overflow-y-auto max-h-48">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-400">Aucun résultat</div>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => { onChange(opt.id); setOpen(false); setSearch(''); }}
                  className={`flex items-center gap-2.5 w-full px-3 py-2 text-sm text-left transition-colors hover:bg-iris-50 ${
                    opt.id === value ? 'bg-iris-50 font-semibold text-iris-700' : 'text-gray-700'
                  }`}
                >
                  {renderAvatar(opt, 28)}
                  <span className="truncate">{opt.name}</span>
                  {opt.id === value && (
                    <svg className="ml-auto h-4 w-4 text-iris-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Color circles picker ── */
function ColorPicker({ value, onChange, options }) {
  return (
    <div className="flex flex-wrap gap-2.5">
      {options.map((c) => {
        const isSelected = value === c.id;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onChange(c.id)}
            title={c.name}
            className="relative transition-transform hover:scale-110 focus:outline-none"
            style={{ width: 32, height: 32 }}
          >
            <div
              style={{
                width: 32, height: 32, borderRadius: '50%',
                background: c.id,
                border: isSelected ? '3px solid #7e22ce' : '2px solid #e5e7eb',
                boxShadow: isSelected ? '0 0 0 2px #e9d5ff' : 'none',
                transition: 'all 150ms',
              }}
            />
            {isSelected && (
              <svg
                className="absolute inset-0 m-auto"
                width="14" height="14" viewBox="0 0 20 20"
                fill="white" stroke="white" strokeWidth="1"
              >
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ── Workload slider (0-100%) ── */
function WorkloadField({ value, onChange }) {
  const pct = value ?? 100;
  const color = pct >= 90 ? '#dc2626' : pct >= 70 ? '#d97706' : pct >= 40 ? '#2563eb' : '#6b7280';

  return (
    <div>
      <div className="flex items-center gap-3">
        <span className="text-[10px] font-semibold text-gray-400 w-5 text-right flex-shrink-0">0</span>
        <div className="flex-1 relative py-2">
          <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-gray-200" />
          <div
            className="absolute left-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full transition-all"
            style={{ width: pct + '%', background: color }}
          />
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={pct}
            onChange={(e) => onChange(parseInt(e.target.value, 10))}
            className="relative w-full h-4 appearance-none cursor-pointer bg-transparent z-10"
            style={{
              WebkitAppearance: 'none',
              background: 'transparent',
            }}
          />
        </div>
        <span className="text-[10px] font-semibold text-gray-400 w-7 flex-shrink-0">100</span>
        <div className="flex items-center gap-1 min-w-[60px] ml-1">
          <input
            type="number"
            min={0}
            max={100}
            value={pct}
            onChange={(e) => {
              const v = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
              onChange(v);
            }}
            className="w-12 text-sm text-center border border-gray-300 rounded-md py-1 focus:border-iris-500 focus:ring-1 focus:ring-iris-500/20 focus:outline-none"
          />
          <span className="text-sm font-semibold" style={{ color }}>%</span>
        </div>
      </div>
      <style>{`
        .WorkloadField input[type=range]::-webkit-slider-thumb {
          -webkit-appearance: none; width: 18px; height: 18px; border-radius: 50%;
          background: white; border: 3px solid ${color}; box-shadow: 0 1px 3px rgba(0,0,0,.15); cursor: pointer;
        }
        .WorkloadField input[type=range]::-moz-range-thumb {
          width: 18px; height: 18px; border-radius: 50%;
          background: white; border: 3px solid ${color}; box-shadow: 0 1px 3px rgba(0,0,0,.15); cursor: pointer;
        }
        .WorkloadField input[type=range]::-webkit-slider-runnable-track { background: transparent; }
        .WorkloadField input[type=range]::-moz-range-track { background: transparent; }
      `}</style>
    </div>
  );
}

export function FormModal({ fields, data, onClose }) {
  const [formData, setFormData] = useState(() => {
    const initial = { ...data };
    fields.forEach((f) => {
      if (f.id && f.type === 'date' && initial[f.id]) {
        initial[f.id] = toLocalDate(initial[f.id]);
      }
    });
    return initial;
  });
  const firstInputRef = useRef(null);

  useEffect(() => {
    firstInputRef.current?.focus();
    const handler = (e) => { if (e.key === 'Escape') onClose({ canceled: true, result: null }); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    const result = { ...formData };
    fields.forEach((f) => {
      if (f.id && f.type === 'date' && result[f.id]) {
        result[f.id] = fromLocalDate(result[f.id]);
      }
    });
    onClose({ canceled: false, result });
  }, [formData, fields, onClose]);

  const update = (id, value) => setFormData((prev) => {
    const next = { ...prev, [id]: value };
    if (id === 'projectId') {
      const taskField = fields.find((f) => f.filterByField === 'projectId');
      if (taskField) {
        const taskStillValid = (taskField.options || []).some(
          (opt) => opt.id === prev[taskField.id] && (!opt.projectId || opt.projectId === value),
        );
        if (!taskStillValid) next[taskField.id] = '';
      }
    }
    return next;
  });

  let isFirstInput = true;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-iris-900/40"
        onClick={() => onClose({ canceled: true, result: null })}
      />
      <div className="relative bg-white rounded-xl shadow-2xl border border-gray-200/75 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit} className="p-7">
          {fields.map((field, idx) => {
            if (field.type === 'title') {
              const isMain = idx === 0;
              return (
                <div
                  key={idx}
                  className={
                    isMain
                      ? 'text-lg font-bold text-gray-900 pb-3 mb-4 border-b-2 border-iris-200'
                      : 'text-[11px] font-bold uppercase tracking-wider text-gray-500 mt-5 mb-1 pb-1.5 border-b border-gray-100'
                  }
                >
                  {field.name}
                </div>
              );
            }

            if (field.dependsOn && !formData[field.dependsOn]) {
              return null;
            }

            const refProp = isFirstInput ? firstInputRef : null;
            if (isFirstInput && field.id) isFirstInput = false;

            const label = (
              <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1 ml-0.5">
                {field.name}
              </label>
            );

            if (field.type === 'resourceSelect') {
              return (
                <div key={field.id} className="mb-3">
                  {label}
                  <ResourceField
                    value={formData[field.id] ?? ''}
                    onChange={(v) => update(field.id, v)}
                    options={field.options || []}
                    inputRef={refProp}
                  />
                </div>
              );
            }

            if (field.type === 'colorCircles') {
              return (
                <div key={field.id} className="mb-3">
                  {label}
                  <ColorPicker
                    value={formData[field.id] ?? ''}
                    onChange={(v) => update(field.id, v)}
                    options={field.options || []}
                  />
                </div>
              );
            }

            if (field.type === 'workload') {
              return (
                <div key={field.id} className="mb-3 WorkloadField">
                  {label}
                  <WorkloadField
                    value={formData[field.id] ?? 100}
                    onChange={(v) => update(field.id, v)}
                  />
                </div>
              );
            }

            if (field.type === 'date') {
              return (
                <div key={field.id} className="mb-3">
                  {label}
                  <input
                    type="date"
                    ref={refProp}
                    value={formData[field.id] ?? ''}
                    onChange={(e) => update(field.id, e.target.value)}
                    className="input"
                  />
                </div>
              );
            }

            if (field.type === 'select') {
              let visibleOptions = field.options || [];
              if (field.filterByField) {
                const filterVal = formData[field.filterByField] || '';
                visibleOptions = visibleOptions.filter(
                  (opt) => !opt[field.filterByField] || opt[field.filterByField] === filterVal,
                );
              }
              return (
                <div key={field.id} className="mb-3">
                  {label}
                  <select
                    ref={refProp}
                    value={formData[field.id] ?? ''}
                    onChange={(e) => update(field.id, e.target.value)}
                    className="select"
                  >
                    {visibleOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>{opt.name}</option>
                    ))}
                  </select>
                </div>
              );
            }

            if (field.type === 'textarea') {
              return (
                <div key={field.id} className="mb-3">
                  {label}
                  <textarea
                    ref={refProp}
                    value={formData[field.id] ?? ''}
                    onChange={(e) => update(field.id, e.target.value)}
                    rows={3}
                    className="textarea"
                  />
                </div>
              );
            }

            return (
              <div key={field.id} className="mb-3">
                {label}
                <input
                  type="text"
                  ref={refProp}
                  value={formData[field.id] ?? ''}
                  onChange={(e) => update(field.id, e.target.value)}
                  className="input"
                />
              </div>
            );
          })}

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => onClose({ canceled: true, result: null })}
              className="btn btn-secondary"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn btn-primary"
            >
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function ConfirmModal({ message, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose({ canceled: true }); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-iris-900/40" onClick={() => onClose({ canceled: true })} />
      <div className="relative bg-white rounded-xl shadow-2xl border border-gray-200/75 w-full max-w-sm mx-4 p-7">
        <p className="text-sm text-gray-700 mb-6">{message}</p>
        <div className="flex justify-end gap-2">
          <button
            onClick={() => onClose({ canceled: true })}
            className="btn btn-secondary"
          >
            Annuler
          </button>
          <button
            onClick={() => onClose({ canceled: false })}
            className="btn btn-danger"
          >
            Confirmer
          </button>
        </div>
      </div>
    </div>
  );
}

export function useModalManager() {
  const [modalState, setModalState] = useState(null);
  const resolverRef = useRef(null);

  const showFormModal = useCallback((fields, data) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setModalState({ type: 'form', fields, data });
    });
  }, []);

  const showConfirmModal = useCallback((message) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setModalState({ type: 'confirm', message });
    });
  }, []);

  const handleModalClose = useCallback((result) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setModalState(null);
  }, []);

  const modalPortal = modalState
    ? createPortal(
        modalState.type === 'form'
          ? <FormModal fields={modalState.fields} data={modalState.data} onClose={handleModalClose} />
          : <ConfirmModal message={modalState.message} onClose={handleModalClose} />,
        document.body,
      )
    : null;

  return { showFormModal, showConfirmModal, modalPortal };
}
